/**
 * The last few client-side errors, kept in memory only (never stored, never
 * sent except inside a feedback report the person chooses to send). Bug
 * reports carry them so Ivin sees what broke without asking.
 *
 * Listens to uncaught errors, unhandled promise rejections and console.error
 * (React reports render errors there). Only real error objects are read
 * (isRealError), never a plain object or value. Each entry is a short, scrubbed
 * message (redact.js: no URL queries, secrets, emails, tokens or long
 * numbers). From console.error only error objects (their name, code and
 * message) and a short leading label are kept, never the other values code
 * logs alongside. Installed once from main.jsx; App.jsx clears it whenever
 * the account changes, so one person's errors never ride in another's
 * report.
 */
import { clip, redact } from "./redact.js";

export const MAX_ERRORS = 10;
const MAX_MESSAGE = 300;
const MAX_LABEL = 80;

let buffer = [];
let installed = false;

// A real error (an Error or a subclass such as FirebaseError, or a
// DOMException). A plain object with a "message" could be anything a caller
// built (a draft, a form), so it is never read; nor is one dressed up with
// Symbol.toStringTag (the Suite has no other frames whose errors matter).
export const isRealError = (v) =>
  v instanceof Error || (typeof DOMException !== "undefined" && v instanceof DOMException);

const errorText = (v) => {
  if (v == null || typeof v !== "object" || !isRealError(v)) return "";
  const msg = typeof v.message === "string" ? v.message : "";
  if (!msg && !v.code) return "";
  const name = typeof v.name === "string" && v.name !== "Error" ? v.name : (v instanceof Error ? "Error" : "");
  const code = typeof v.code === "string" ? ` [${v.code}]` : "";
  return `${name ? `${name}${code}: ` : code ? `${code.trim()}: ` : ""}${msg}`;
};

// A code label: short, first, and ending in a colon ("users.getByEmail:",
// "[Auth] bootstrapUser:", "Failed to save feedback:"). Any other string a
// caller logs (a value, a name, something typed) is never kept.
const LABEL_RE = /^[A-Za-z0-9_.()[\] -]{1,78}:$/;

/**
 * The text kept for one report of an error: error objects (name, code,
 * message), and a string only when it is the first part and a code label.
 */
const textOf = (v, i) => {
  if (typeof v === "string") return i === 0 && v.length <= MAX_LABEL && LABEL_RE.test(v.trim()) ? v.trim() : "";
  return errorText(v);
};

export function recordError(kind, parts) {
  const list = Array.isArray(parts) ? parts : [parts];
  const message = clip(redact(list.map(textOf).filter(Boolean).join(" ")), MAX_MESSAGE);
  if (!message) return;
  buffer.push({ at: new Date().toISOString(), kind, message });
  if (buffer.length > MAX_ERRORS) buffer = buffer.slice(-MAX_ERRORS);
}

/** A copy of the recent errors, oldest first. */
export function recentErrors() {
  return buffer.map((e) => ({ ...e }));
}

export function clearErrors() {
  buffer = [];
}

export function installErrorBuffer(win = typeof window !== "undefined" ? window : null) {
  if (!win || installed) return;
  installed = true;
  // An uncaught error or rejection is kept when it is an error object. A
  // thrown or rejected plain value (a string, a number) could be anything,
  // so only the fact is kept; so is a browser's "Script error." (another
  // site's script, no details).
  const NOT_AN_ERROR = "A value that is not an error was thrown";
  const asError = (v) => (isRealError(v) ? v : new Error(NOT_AN_ERROR));
  win.addEventListener("error", (e) => {
    if (e?.error == null && e?.message === "Script error.") recordError("error", [new Error("Script error.")]);
    else recordError("error", [asError(e?.error)]);
  });
  win.addEventListener("unhandledrejection", (e) => {
    recordError("rejection", [asError(e?.reason)]);
  });
  const original = win.console?.error;
  if (typeof original === "function") {
    win.console.error = function feedbackConsoleError(...args) {
      try { recordError("console", args); } catch { /* never let the buffer break logging */ }
      return original.apply(this, args);
    };
  }
}
